import React, { useContext, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useHistory, useLocation } from 'react-router-dom';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import { StakingContext } from '../../../contexts/staking/context';
import ExchangeModal from '../../Accounts/modals/exchangeModal';
import { featuresModales, mainStakingCurrency } from '../constants';
import {
  useLedgerFilteredCurrencies,
  useLedgerFilteredWallets,
} from '../../../utilities/sharedHooks/ledgerWallets';
import { WALLETS_ALL } from '../../Accounts/queries';
import {
  SETTINGS_SUPPORTED_CURRENCIES_QUERY_KEY,
  USER_PREFERENCES,
} from '../../../utilities/sharedQueries';
import { currencies, walletTypes } from '../../../utilities/constants';
import WalletDepositModal from '../../Accounts/modals/WalletsWithdrawDepositModal';
import ModalLayout from '../../shared/ModalLayout';
import InfoPopup from '../../shared/Modals/InfoPopup';
import { useLedgerAccounts } from '../../../utilities/sharedHooks/ledgerSDKHooks';

const FeatureMainLayout = ({ children, className }) => {
  const history = useHistory();

  const { t: translate } = useTranslation();

  const { state: locationState } = useLocation();

  const {
    state,
    openScreen,
    toggleModal,
    updateMainStakingWallet,
    updateShowRewardInfo,
    updateWallets,
  } = useContext(StakingContext);

  const { data: wallets, refetch: refetchAllWallets } = useLedgerFilteredWallets(WALLETS_ALL);

  const [ledgerAccounts] = useLedgerAccounts();

  const { data: supportedCurrencies } = useLedgerFilteredCurrencies([
    SETTINGS_SUPPORTED_CURRENCIES_QUERY_KEY,
  ]);

  const { data: userPref } = useQuery(USER_PREFERENCES);
  const fiatCurrencyCode = userPref?.displayCurrency || 'EUR';

  const mainStakingWallet = useMemo(() => {
    if (wallets) {
      return wallets?.find((w) => w.coin.toLowerCase() === mainStakingCurrency);
    }
    return {};
  }, [wallets]);

  const ledgerAccount = useMemo(() => {
    if (ledgerAccounts && mainStakingWallet) {
      if (
        supportedCurrencies &&
        supportedCurrencies[mainStakingWallet?.coin] &&
        supportedCurrencies[mainStakingWallet?.coin].is_erc20_token &&
        ledgerAccounts[currencies.ETH.icon.toLowerCase()]
      ) {
        return ledgerAccounts[currencies.ETH.icon.toLowerCase()];
      }
      return ledgerAccounts[mainStakingWallet?.coin];
    }
    return null;
  }, [mainStakingWallet, ledgerAccounts, supportedCurrencies]);

  const renderedComponent = useMemo(() => {
    if (Array.isArray(children)) {
      return children?.filter((child) => child.props.keyName === state?.currentScreen);
    }
    return [children]?.filter((child) => child.props.keyName === state?.currentScreen);
  }, [children, state?.currentScreen]);

  useEffect(() => {
    if (!locationState?.initScreen) {
      history.push('/');
    } else {
      openScreen(locationState?.initScreen);
      refetchAllWallets();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (wallets) {
      updateWallets(wallets);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wallets]);

  useEffect(() => {
    if (mainStakingWallet) {
      updateMainStakingWallet(mainStakingWallet);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mainStakingWallet]);

  const closeStakingFeatures = () => history.push('/dashboard/staking');

  const rewardInfoOptions = {
    withBack: false,
    isScrollable: false,
    isButtonInFooter: false,
    content: () => (
      <div className="mt-4">
        <p className="text-white text-f5 mb-3">{translate('staking_reward_note')}</p>
      </div>
    ),
  };

  return (
    <div className="d-flex flex-row main-layout">
      {/* browser only */}
      <div
        className="position-absolute d-none d-md-block"
        onClick={closeStakingFeatures}
        onKeyDown={closeStakingFeatures}
        role="button"
        style={{ top: '4%', right: '3%', zIndex: '10' }}
        tabIndex={-1}>
        <i
          className="close-btn cursor-pointer text-hover icon icon-close-other text-dark text-f4"
          aria-label="Close"
        />
      </div>
      <div className="d-flex flex-column flex-fill align-items-center h-100vh position-relative">
        <div
          className={`d-flex flex-column w-100 position-relative h-100 px-3 px-md-0 cryptodraft-main-layout ${className}`}
          style={{
            maxWidth: '33.5rem',
            transitionProperty: 'margin',
            transitionDuration: '300ms',
          }}>
          {renderedComponent}
        </div>
      </div>
      {state?.exchangeModalOpened && (
        <ExchangeModal
          hide={() => toggleModal(featuresModales.EXCHANGE, false)}
          fiatCurrencyCode={fiatCurrencyCode}
          supportedCurrencies={supportedCurrencies}
          forcedDestinationWallet={mainStakingWallet}
        />
      )}
      {state?.addFundsModalOpened && (
        <WalletDepositModal
          hide={() => toggleModal(featuresModales.ADD_FUNDS, false)}
          walletsType={walletTypes.CRYPTO}
          wallet={mainStakingWallet}
          ledgerAccount={ledgerAccount}
          isDeposit
        />
      )}
      {state?.showRewardInfo && (
        <ModalLayout
          rootClassName="d-block d-md-none"
          isVisible
          onHide={() => updateShowRewardInfo(false)}
          currentScreen="StakingInfoPopup">
          <InfoPopup keyName="StakingInfoPopup" options={rewardInfoOptions} />
        </ModalLayout>
      )}
    </div>
  );
};

export default FeatureMainLayout;

FeatureMainLayout.propTypes = {
  children: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  className: PropTypes.string,
};

FeatureMainLayout.defaultProps = {
  children: null,
  className: '',
};
